"use client";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

interface PillTabScrollerProps {
  // Accessible name of the tablist.
  ariaLabel: string;
  // The role="tab" buttons (style them with `pillTabClass`).
  children: ReactNode;
  // Accessible names of the scroll arrows.
  prevLabel?: string;
  nextLabel?: string;
}

// Shared look for one pill tab: solid red when selected, white otherwise.
export const pillTabClass = (isActive: boolean) =>
  cn(
    "shrink-0 cursor-pointer snap-start rounded-full border-[1.5px] px-[18px] py-2.5 text-[15px] font-bold whitespace-nowrap transition-colors",
    isActive
      ? "border-primary bg-primary text-white"
      : "border-line bg-white text-ink hover:border-line-warm",
  );

const arrowClass =
  "absolute top-1/2 z-10 grid size-9 -translate-y-1/2 cursor-pointer place-items-center rounded-full bg-white text-ink shadow-card transition-opacity hover:bg-paper disabled:cursor-default disabled:opacity-40 disabled:hover:bg-white";

// Soft fade on whichever edge has more tabs. A mask (not a coloured
// gradient) so it works on any band colour, light or dark.
const fadeClass = {
  both: "[mask-image:linear-gradient(to_right,transparent,black_56px,black_calc(100%-56px),transparent)]",
  start: "[mask-image:linear-gradient(to_right,transparent,black_56px)]",
  end: "[mask-image:linear-gradient(to_left,transparent,black_56px)]",
};

// A one-line, sideways-scrolling row of pill tabs (instead of wrapping onto
// several lines). Centred when everything fits; when it overflows, round
// arrow buttons sit on both sides (the one with nothing further to show is
// dimmed) and the row fades out under them. Swipe works on touch, and
// arrow-key focus scrolls the focused tab into view.
const PillTabScroller = ({
  ariaLabel,
  children,
  prevLabel = "Previous tabs",
  nextLabel = "Next tabs",
}: PillTabScrollerProps) => {
  const initialState = { canPrev: false, canNext: false };
  const [state, setState] = useState(initialState);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const canPrev = el.scrollLeft > 4;
    const canNext = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setState((state) =>
      state.canPrev === canPrev && state.canNext === canNext
        ? state
        : { ...state, canPrev, canNext },
    );
  }, []);

  // Re-measure on resize and scroll (callbacks, not during the effect).
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    el.addEventListener("scroll", measure, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener("scroll", measure);
    };
  }, [measure]);

  const scrollByPage = (direction: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    el.scrollBy({
      left: direction * el.clientWidth * 0.7,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  };

  const isOverflowing = state.canPrev || state.canNext;
  const fade =
    state.canPrev && state.canNext
      ? fadeClass.both
      : state.canPrev
        ? fadeClass.start
        : state.canNext
          ? fadeClass.end
          : undefined;

  return (
    <div className="relative">
      {isOverflowing && (
        <button
          type="button"
          aria-label={prevLabel}
          disabled={!state.canPrev}
          onClick={() => scrollByPage(-1)}
          className={cn(arrowClass, "left-0")}
        >
          <ChevronLeft className="size-5" aria-hidden="true" />
        </button>
      )}
      <div
        ref={scrollerRef}
        className={cn(
          "snap-x overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          isOverflowing && "scroll-px-11 px-11",
          fade,
        )}
      >
        <div
          role="tablist"
          aria-label={ariaLabel}
          className="mx-auto flex w-max gap-2 py-1"
        >
          {children}
        </div>
      </div>
      {isOverflowing && (
        <button
          type="button"
          aria-label={nextLabel}
          disabled={!state.canNext}
          onClick={() => scrollByPage(1)}
          className={cn(arrowClass, "right-0")}
        >
          <ChevronRight className="size-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default PillTabScroller;
